import React, { useEffect } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { Zone } from "../../../API";
import DeleteZoneContainer from "../../../pages/devices/components/DeleteZoneContainer";
import DeleteIconButton from "../icons/DeleteIconButton";
import S3Image from "../media/S3Image";
import RouterLink from "../RouterLink";
import ZoneStatusToggle from "./ZoneStatusToggle";
import { useGetAnnotations } from "../../../pages/devices/hooks/useGetAnnotations";

interface ExtendedZone extends Zone {
  deviceName: string;
  nodeId: string;
  hasZoom: boolean;
}

interface ZoneCardProps {
  datum: ExtendedZone;
  link: string;
  deviceStatus: boolean;
}

const ZoneCard: React.FC<ZoneCardProps> = ({
  datum,
  link,
  deviceStatus,
}): JSX.Element => {
  const { data: fetchAnnotationsData, fetchAnnotations } = useGetAnnotations(
    {}
  );

  useEffect(() => {
    fetchAnnotations({ zoneId: datum.id, serviceId: datum.serviceId ?? "" });
  }, [datum.id, datum.serviceId]);

  const annotationsData = fetchAnnotationsData?.getAnnotations?.items?.filter(
    item => item.zoneId === datum.id
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        "&:hover .delete-zone-button": {
          display: "block",
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <RouterLink sx={{ whiteSpace: "pre-wrap" }} to={link}>
          <Box
            sx={{
              width: "244px",
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              justifyContent: "center",
              backgroundColor: "otherDisabledBackground.main",
              border: 1,
              borderColor: "otherOutlineBorder.main",
              borderRadius: "8px",
              height: "182px",
            }}
          >
            <S3Image
              fixedHeight={"182px"}
              fixedWidth={244}
              variant="annotations"
              annotationsData={annotationsData}
              s3Key={datum.signedUrlKeyFrame ?? ""}
              deviceStatus={deviceStatus}
            />
          </Box>
        </RouterLink>

        <Box
          className="delete-zone-button"
          sx={{
            display: "none",
            position: "absolute",
            top: "10px",
            cursor: "pointer",
            right: "10px",
          }}
        >
          <DeleteZoneContainer
            serviceId={datum.serviceId ?? ""}
            noRedirection
            zoneId={datum.id}
            zoneName={datum.name}
            zoneDeviceId={datum.deviceId}
            nodeId={datum.nodeId}
          >
            <DeleteIconButton />
          </DeleteZoneContainer>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <RouterLink to={link} title={datum.name} />
        <ZoneStatusToggle params={datum} />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
        }}
      >
        <Typography variant="inputLabel">Pan:</Typography>
        <Typography variant="body2Regular">{datum.pan}</Typography>
        <Typography variant="inputLabel">Tilt:</Typography>
        <Typography variant="body2Regular">{datum.tilt}</Typography>
        {datum.hasZoom && datum.zoom && (
          <>
            <Typography variant="inputLabel">Zoom:</Typography>
            <Typography variant="body2Regular">{datum.zoom}</Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ZoneCard;
